import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { usePartyApi } from "../api/party-api";
import type { CustomerProfile } from "../api/contracts";

// The party principal — MINIMAL by design (ADR 0005). The console's PrincipalProvider
// loads a staff `Me` (permissions/grants) to gate surfaces; the party realm has NO
// permissions/grants — "the token authenticates a subject; the database authorizes a
// party." So the customer's principal is just: authenticated + the masked profile
// (display name), from GET /v1/customer/profile. There is NOTHING to gate client-side;
// the party surfaces render masked/scoped reads and submit intents, nothing more.
export interface PartyPrincipalState {
  profile: CustomerProfile | null;
  isLoading: boolean;
  error: unknown;
}

const PartyPrincipalContext = createContext<PartyPrincipalState | null>(null);

export function PartyPrincipalProvider({ children }: { children: ReactNode }) {
  const api = usePartyApi();
  const { data, isLoading, error } = useQuery({
    queryKey: ["customer", "profile"],
    queryFn: () => api.get<CustomerProfile>("/v1/customer/profile"),
    staleTime: 5 * 60_000,
    retry: false,
  });
  return (
    <PartyPrincipalContext.Provider value={{ profile: data ?? null, isLoading, error }}>
      {children}
    </PartyPrincipalContext.Provider>
  );
}

/** Test seam — inject a fixed party principal without a query/network. */
export function PartyPrincipalContextProvider({
  value,
  children,
}: {
  value: PartyPrincipalState;
  children: ReactNode;
}) {
  return <PartyPrincipalContext.Provider value={value}>{children}</PartyPrincipalContext.Provider>;
}

export function usePartyPrincipal(): PartyPrincipalState {
  const ctx = useContext(PartyPrincipalContext);
  if (!ctx) throw new Error("usePartyPrincipal must be used within a PartyPrincipalProvider");
  return ctx;
}
